import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Loader2, Film, AlertCircle, Trash2, Palette, Play } from 'lucide-react';
import { MediaBrandManager, type MediaBrandProfile } from '@rehub/branded-viewer';
import {
  fetchProjects,
  deleteProject,
  fetchBrandProfiles,
  createBrandProfile,
  updateBrandProfile,
  deleteBrandProfile,
  uploadBrandProfileLogo,
} from '../services/supabase';
import { TEMPLATES } from '../constants';
import type { PropertyCutProject, TemplateId } from '../types';

interface DashboardProps {
  userId: string;
  onNewVideo: () => void;
  onOpenProject: (id: string) => void;
}

export default function Dashboard({ userId, onNewVideo, onOpenProject }: DashboardProps) {
  const [projects, setProjects] = useState<PropertyCutProject[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Brand manager (reusable brand profiles, shared across ReHub apps).
  const [showBrandManager, setShowBrandManager] = useState(false);
  const [brands, setBrands] = useState<MediaBrandProfile[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);

  const loadBrands = useCallback(async () => {
    setBrandsLoading(true);
    try {
      const data = await fetchBrandProfiles(userId);
      setBrands(data);
      return data;
    } finally {
      setBrandsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (showBrandManager && brands.length === 0) void loadBrands();
  }, [showBrandManager, brands.length, loadBrands]);

  useEffect(() => {
    let cancelled = false;
    fetchProjects()
      .then((rows) => { if (!cancelled) setProjects(rows); })
      .catch((err) => { if (!cancelled) setError(err?.message ?? 'Failed to load projects'); });
    return () => { cancelled = true; };
  }, []);

  // Auto-cancel the "click again to confirm" state after 3s.
  useEffect(() => {
    if (!confirmDeleteId) return;
    const t = setTimeout(() => setConfirmDeleteId(null), 3000);
    return () => clearTimeout(t);
  }, [confirmDeleteId]);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    const prev = projects;
    // Optimistic removal so the UI feels snappy.
    setProjects((cur) => (cur ? cur.filter((p) => p.id !== id) : cur));
    try {
      await deleteProject(id);
    } catch (err: any) {
      console.warn('[propertycut] deleteProject failed:', err);
      setProjects(prev);
      setError(err?.message ?? 'Could not delete that project');
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Your projects</h1>
          <p className="text-sm text-slate-400 mt-1">
            Choose a format, add media, then export.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowBrandManager(true)}
            className="inline-flex items-center gap-2 px-3 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-sm font-medium transition-colors"
          >
            <Palette className="w-4 h-4" />
            <span className="hidden sm:inline">Brands</span>
          </button>
          <button
            onClick={onNewVideo}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-teal-400 hover:bg-teal-300 text-slate-950 text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            New video
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-300 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {projects === null && !error && (
        <div className="grid place-items-center py-16">
          <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
        </div>
      )}

      {projects && projects.length === 0 && (
        <EmptyState onNewVideo={onNewVideo} />
      )}

      {projects && projects.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              isConfirming={confirmDeleteId === project.id}
              isDeleting={deletingId === project.id}
              onClick={() => onOpenProject(project.id)}
              onDeleteClick={() => {
                if (confirmDeleteId === project.id) {
                  void handleDelete(project.id);
                } else {
                  setConfirmDeleteId(project.id);
                }
              }}
            />
          ))}
        </div>
      )}

      {showBrandManager && (
        <MediaBrandManager
          userId={userId}
          brands={brands}
          loading={brandsLoading}
          onClose={() => setShowBrandManager(false)}
          onLoadBrands={loadBrands}
          onCreateBrand={(input) => createBrandProfile(userId, input)}
          onUpdateBrand={(id, input) => updateBrandProfile(userId, id, input)}
          onDeleteBrand={(id) => deleteBrandProfile(userId, id)}
          onUploadLogo={(file) => uploadBrandProfileLogo(file, userId)}
          accentColor="#007d72"
        />
      )}
    </div>
  );
}

function EmptyState({ onNewVideo }: { onNewVideo: () => void }) {
  return (
    <div className="rounded-2xl border border-slate-800/80 bg-slate-900/30 px-6 py-16 text-center">
      <div className="w-12 h-12 mx-auto rounded-xl bg-teal-500/10 grid place-items-center mb-4">
        <Film className="w-5 h-5 text-teal-400" />
      </div>
      <h2 className="text-lg font-medium">No videos yet</h2>
      <p className="text-sm text-slate-400 mt-1 max-w-sm mx-auto">
        Pick a format, add photos or clips, and export an MP4.
      </p>
      <button
        onClick={onNewVideo}
        className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-teal-400 hover:bg-teal-300 text-slate-950 text-sm font-medium transition-colors"
      >
        <Plus className="w-4 h-4" />
        Create your first video
      </button>
    </div>
  );
}

// Soft, template-themed gradients used as a placeholder when a project
// doesn't have a thumbnail yet (i.e. before any render has completed).
const TEMPLATE_GRADIENTS: Record<TemplateId, string> = {
  'classic-listing':
    'bg-[radial-gradient(ellipse_at_top_left,rgba(148,229,207,0.22),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(0,125,114,0.22),transparent_55%)]',
  'social-reel':
    'bg-[radial-gradient(ellipse_at_top_left,rgba(0,125,114,0.28),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(148,229,207,0.16),transparent_55%)]',
  'square-highlight':
    'bg-[radial-gradient(ellipse_at_top_left,rgba(148,229,207,0.16),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(0,125,114,0.18),transparent_55%)]',
};

function formatDate(value: string): string {
  try {
    return new Date(value).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function ProjectCard({
  project,
  isConfirming,
  isDeleting,
  onClick,
  onDeleteClick,
}: {
  project: PropertyCutProject;
  isConfirming: boolean;
  isDeleting: boolean;
  onClick: () => void;
  onDeleteClick: () => void;
}) {
  const template = TEMPLATES[project.template_id];
  const title = project.title || project.listing_data?.address || template?.name || 'Untitled';
  const gradient = TEMPLATE_GRADIENTS[project.template_id] ?? '';
  const statusLabel = project.status.replace('_', ' ');

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="relative text-left rounded-xl border border-slate-800/80 bg-slate-900/30 hover:bg-slate-900/60 hover:border-slate-700 transition-colors overflow-hidden group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/60"
    >
      <div className={`aspect-video bg-slate-900 relative overflow-hidden ${gradient}`}>
        {project.thumbnail_url ? (
          project.thumbnail_is_video ? (
            <video
              // #t=0.1 nudges the browser to render an early frame as the
              // poster, so a video-only project still gets a real preview.
              src={`${project.thumbnail_url}#t=0.1`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              muted
              playsInline
              preload="metadata"
            />
          ) : (
            <img
              src={project.thumbnail_url}
              alt=""
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          )
        ) : null}
        {project.thumbnail_url && project.thumbnail_is_video && (
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <span className="w-9 h-9 rounded-full bg-black/45 backdrop-blur-sm border border-white/20 grid place-items-center">
              <Play className="w-4 h-4 text-white fill-white/90" />
            </span>
          </div>
        )}
        {!project.thumbnail_url && (
          <div className="absolute inset-0 grid place-items-center">
            <div className="flex flex-col items-center gap-2 text-slate-300/70">
              <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 grid place-items-center backdrop-blur-sm">
                <Film className="w-4 h-4" />
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-300/60">
                {project.output_format === 'vertical'
                  ? 'Portrait'
                  : project.output_format === 'square'
                    ? 'Square'
                    : project.output_format === 'landscape'
                      ? 'Landscape'
                      : template?.name ?? 'Video'}
              </span>
            </div>
          </div>
        )}
        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-black/60 backdrop-blur border border-white/10">
          {project.output_format === 'vertical'
            ? 'Portrait'
            : project.output_format === 'square'
              ? 'Square'
              : project.output_format === 'landscape'
                ? 'Landscape'
                : template?.name ?? project.template_id}
        </span>
      </div>
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{title}</p>
          <p className="text-xs text-slate-500 mt-0.5 capitalize">
            {statusLabel}
            <span className="text-slate-700 mx-1.5">·</span>
            <span className="normal-case text-slate-500">{formatDate(project.created_at)}</span>
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (isDeleting) return;
          onDeleteClick();
        }}
        disabled={isDeleting}
        title={isConfirming ? 'Click again to confirm delete' : 'Delete project'}
        aria-label={isConfirming ? 'Confirm delete' : 'Delete project'}
        className={`absolute top-2 left-2 p-1.5 rounded-md backdrop-blur-sm transition-all
          ${isConfirming
            ? 'bg-red-500/90 text-white opacity-100 hover:bg-red-500'
            : 'bg-black/60 text-slate-300 hover:text-red-300 hover:bg-black/80 opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
      >
        {isDeleting ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Trash2 className="w-3.5 h-3.5" />
        )}
      </button>
    </div>
  );
}
